import type { Map } from 'ol';
import { fromString } from 'ol/transform';

export async function createMapImage(map: Map): Promise<Blob> {
    const viewport = map.getViewport();
    const size = map.getSize();
    const overlayContainer = viewport?.querySelector<HTMLElement>('.ol-overlaycontainer');
    const layers = viewport?.querySelectorAll<HTMLCanvasElement>('.ol-layer canvas');
    if (!size || !size[0] || !size[1] || !layers?.length || !overlayContainer) {
        throw new Error('Kartbildet kan ikke lages fordi kartlerret mangler.');
    }

    const canvas = document.createElement('canvas');
    const pixelRatio = map.getPixelRatio();
    canvas.width = Math.round(size[0] * pixelRatio);
    canvas.height = Math.round(size[1] * pixelRatio);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Kartbildet kan ikke lages fordi 2D-kontekst mangler.');
    }
    ctx.scale(canvas.width / size[0], canvas.height / size[1]);

    layers.forEach(layerCanvas => {
        if (!layerCanvas.width || !layerCanvas.height) return;
        const container = layerCanvas.parentElement;
        ctx.save();
        const opacity = container?.style.opacity || layerCanvas.style.opacity;
        ctx.globalAlpha = opacity ? Number(opacity) : 1;
        if (container?.style.backgroundColor) {
            ctx.fillStyle = container.style.backgroundColor;
            ctx.fillRect(0, 0, size[0], size[1]);
        }
        const transform = layerCanvas.style.transform;
        if (transform && transform !== 'none') {
            const matrix = fromString(transform);
            if (
                !transform.startsWith('matrix(') ||
                matrix.length !== 6 ||
                matrix.some(value => !Number.isFinite(value))
            ) {
                throw new Error('Kartbildet har en ugyldig lagtransformasjon.');
            }
            ctx.transform(
                matrix[0],
                matrix[1],
                matrix[2],
                matrix[3],
                matrix[4],
                matrix[5]
            );
        } else {
            const width = parseFloat(layerCanvas.style.width) || layerCanvas.width;
            const height = parseFloat(layerCanvas.style.height) || layerCanvas.height;
            ctx.scale(width / layerCanvas.width, height / layerCanvas.height);
        }
        ctx.drawImage(layerCanvas, 0, 0);
        ctx.restore();
    });

    const { default: html2canvas } = await import('html2canvas');
    const overlayCanvas = await html2canvas(overlayContainer, {
        backgroundColor: null,
        scale: pixelRatio,
    });
    ctx.drawImage(overlayCanvas, 0, 0, size[0], size[1]);

    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Kartbildet kunne ikke konverteres til PNG.'));
            }
        });
    });
}
